<template>
  <MakerCartGuideLayout
    pageTitle="Conducting a Maker Activity"
    heroImage="/images/maker-cart-guide/frame-10000064021.png"
    previousUrl="/makercart/guide/7"
    nextUrl="/makercart/guide/9"
  >
    <div class="x-guide8-content">
      <!-- Intro Section -->
      <div class="x-intro-section">
        <h2 class="x-section-title">Role 2: Posing Questions</h2>
        <p class="x-section-text">
          Effective questioning is the cornerstone of meaningful learning. This
          section explores how to craft powerful questions that spark curiosity,
          ignite imagination, and guide learners towards deeper understanding
          during maker activities. We will examine the power of open-ended
          questions, explore different question types, and discuss strategies
          for facilitating productive discussions and inquiry.
        </p>
      </div>

      <!-- Navigation Card -->
      <div class="x-nav-card">
        <div class="x-card-header-bar">
          <div class="x-card-title-area">
            <h2 v-if="currentCardIndex === 0" class="x-card-title">
              Creating an Atmosphere for Sharing Ideas
            </h2>
            <h2 v-if="currentCardIndex === 1" class="x-card-title">
              Types of Questions
            </h2>
            <h2 v-if="currentCardIndex === 2" class="x-card-title">
              Ask investigable questions
            </h2>
          </div>

          <div class="x-card-controls">
            <button
              class="x-control-btn"
              :class="{ 'x-btn-disabled': currentCardIndex === 0 }"
              @click="goToPreviousCard"
              :disabled="currentCardIndex === 0"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 18L9 12L15 6"
                  :stroke="currentCardIndex === 0 ? '#4C4DDC' : '#FFFFFF'"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <button
              class="x-control-btn"
              :class="{ 'x-btn-disabled': currentCardIndex === 2 }"
              @click="goToNextCard"
              :disabled="currentCardIndex === 2"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9 18L15 12L9 6"
                  :stroke="currentCardIndex === 2 ? '#4C4DDC' : '#FFFFFF'"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div class="x-card-divider"></div>

        <div class="x-card-body">
          <!-- Card 1 Content -->
          <div v-if="currentCardIndex === 0" class="x-card-content">
            <p>
              Encourage students to write down all of their questions. Reassure
              them, if necessary, that no question is “wrong.”
            </p>
            <p>Try using these strategies:</p>
            <ol>
              <li>
                <b>Use sufficient “wait time” after asking a question.</b>
              </li>
              <li>
                <b
                  >Accept all ideas as equally worthy of consideration, whether
                  scientifically correct or not.</b
                >
              </li>
              <li>
                <b
                  >Consider ideas collectively as “our” ideas; don’t identify
                  them with individual students.</b
                >
              </li>
              <li>
                <b
                  >Set an example by being patient, sympathetic, encouraging,
                  and fair.</b
                >
              </li>
            </ol>
          </div>

          <!-- Card 2 Content -->
          <div v-if="currentCardIndex === 1" class="x-card-content">
            <ol>
              <li>
                Subject-centred question:
                <ul>
                  <li>“What causes ….”</li>
                  <li>“What makes …”</li>
                  <li>“What affects …”</li>
                  <li>“What is the difference …”</li>
                  <li>“What factors …”</li>
                </ul>
              </li>
              <li>
                Person-centred question:
                <ul>
                  <li>“Why do you think…”</li>
                  <li>“How do you explain…”</li>
                  <li>“Can you predict what will happen if…”</li>
                </ul>
              </li>
              <li>
                Process-centred question:
                <ul>
                  <li>“What happens when…”</li>
                  <li>“What changes do you see when…”</li>
                  <li>“Have you seen any patterns?”</li>
                  <li>“What might you do to…”</li>
                </ul>
              </li>
            </ol>

            <p>
              <b
                >Note that most questions that invite students to express their
                own ideas are open-ended and person-centered.</b
              >
            </p>
            <p>
              You can also try these with your students. Notice how many
              different questions came up. Even though everyone observed the
              same phenomenon, people saw very different things. Notice that the
              phenomenon was very simple, yet it was intriguing enough to pique
              everyone’s curiosity. Notice that there was ample time to observe,
              so a rich variety of questions could be generated by each
              observer.
            </p>
          </div>

          <!-- Card 3 Content -->
          <div v-if="currentCardIndex === 2" class="x-card-content">
            <p>This tip is adapted from <b>Institute for Inquiry</b>.</p>
            <p>
              Questions that lead to taking action and firsthand experiences
              with materials are considered <b>“investigable”</b>. For example,
              questions that begin with “what will happen if . . . “ or contain
              the phrase “does the _____ make a difference” can be investigated.
              The way they are phrased invites one to experiment with materials
              and phenomena.
            </p>
            <p>
              Conversely, questions that do not lead to taking hands-on action
              are considered “non-investigable.” For example, questions that
              begin with “why”—such as “Why is most of the ice balloon
              underneath the water?” or “Why are parts of the ice balloon
              cloudy?” are considered non-investigable. They're stated in a way
              that does not lead directly to hands-on action that would help
              answer the question as stated. Instead, they're requests for
              information or explanations.
            </p>
            <p>
              Using a technique called “variables scan”, we can scan a
              non-investigable question to identify the variables in it—that is,
              examining it to find elements you can change in an experiment.
            </p>

            <div class="x-highlight-text-block">
              <p>
                Example: Why does ice melt so fast when you put it in water?
              </p>
              <ol>
                <li>What are the variables? (1. ice; 2. water or liquid)</li>
                <li>
                  How can the ice be changed? (e.g., size and shape; how much
                  it's submerged.)
                </li>
                <li>
                  How can the water/liquid be changed? (e.g., amount;
                  temperature; adding salt or sugar; different types of liquids)
                </li>
                <li>
                  Turn it into a investigable question
                  <ul>
                    <li>
                      What happens when I change the shape of the ice? Do lots
                      of little pieces melt faster than one big piece? What
                      happens if I put ice in salt water? Does the ice melt
                      faster in a quart of water or a gallon of water?
                    </li>
                  </ul>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  </MakerCartGuideLayout>
</template>

<script setup>
import { ref } from "vue";
import MakerCartGuideLayout from "@/components/maker-cart/MakerCartGuideLayout.vue";

const currentCardIndex = ref(0);
const maxCards = 3;

const goToPreviousCard = () => {
  if (currentCardIndex.value > 0) {
    currentCardIndex.value--;
  }
};

const goToNextCard = () => {
  if (currentCardIndex.value < maxCards - 1) {
    currentCardIndex.value++;
  }
};
</script>

<style scoped>
.x-guide8-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.x-intro-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 0;
}

.x-section-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
}

.x-section-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
  margin: 0;
}

/* Nav Card Styles */
.x-nav-card {
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #d8dae5;
  border-radius: 16px;
  overflow: hidden;
  padding: 16px;
}

.x-card-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  width: 100%;
  margin-bottom: 16px;
}

.x-card-title-area {
  flex: 1;
}

.x-card-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--primary-blue);
  margin: 0;
  white-space: pre-wrap;
}

.x-card-controls {
  display: flex;
  gap: 4px;
  align-items: center;
}

.x-control-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: none;
  background-color: #4c4ddc;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.x-control-btn:hover:not(.x-btn-disabled) {
  opacity: 0.9;
}

.x-btn-disabled {
  background-color: transparent;
  border: 1px solid #4c4ddc;
  cursor: not-allowed;
}

.x-card-divider {
  width: 100%;
  height: 1px;
  background-color: #d8dae5;
  margin-bottom: 16px;
}

.x-card-body {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.x-card-content {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  letter-spacing: 0.08px;
  color: #3b4648;
}

.x-card-content p {
  margin: 0 0 16px 0;
}

.x-card-content p:last-child {
  margin: 0;
}

.x-card-content ol,
.x-card-content ul {
  margin: 0 0 16px 24px;
  padding: 0;
}

.x-card-content li {
  margin-bottom: 8px;
}

.x-highlight-text-block {
  color: #00925e;
}
.x-highlight-text-block p {
  color: #00925e;
}

@media (max-width: 768px) {
  .x-card-header-bar {
    flex-direction: column-reverse;
    gap: 8px;
    align-items: flex-start;
  }

  .x-card-controls {
    align-self: flex-end;
  }

  .x-control-btn {
    width: 40px;
    height: 40px;
  }

  .x-card-title {
    white-space: normal;
  }
}
</style>
